/**
 * Pipeline — Screen 4
 * 8 Kanban columns with HTML5 drag → PATCH stage, filters, stale/live chips.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchLeads, patchLead } from '../../lib/apiHelpers.js';
import { AvatarInitials } from '../../components/AvatarInitials.js';
import { STAGE_CONFIG, stageColor } from '../../components/StageChip.js';
import type { ApiLead, LeadStage } from '@contactrate/shared';
import { usePortal } from '../../portal/PortalContext.js';

const STAGES: { key: LeadStage; label: string; color: string }[] = [
  { key: 'new', label: 'New', color: '#64748B' },
  { key: 'called', label: 'Called', color: '#2E66E0' },
  { key: 'contacted', label: 'Contacted', color: '#0D9488' },
  { key: 'transfer', label: 'Live Transfer', color: '#6D4AEA' },
  { key: 'booked', label: 'Booked', color: '#D97706' },
  { key: 'presented', label: 'Presented', color: '#EA580C' },
  { key: 'follow_up', label: 'Follow Up', color: '#DB2777' },
  { key: 'closed', label: 'Closed', color: '#159662' },
];

function isStale(lead: ApiLead): boolean {
  const FOUR_DAYS = 4 * 24 * 60 * 60 * 1000;
  const age = Date.now() - new Date(lead.updatedAt).getTime();
  const staleStages: LeadStage[] = ['new', 'called', 'contacted', 'follow_up'];
  return age > FOUR_DAYS && staleStages.includes(lead.stage);
}

function getAge(lead: ApiLead): string {
  const ms = Date.now() - new Date(lead.createdAt).getTime();
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return '1d';
  return `${days}d`;
}

type FilterKind = 'all' | 'ai' | 'stale' | 'high';

// ── Pipeline card ─────────────────────────────────────────────────────────────

function PipelineCard({
  lead,
  onDragStart,
}: {
  lead: ApiLead;
  onDragStart: (id: string) => void;
}) {
  const stale = isStale(lead);
  const age = getAge(lead);

  return (
    <div
      draggable
      onDragStart={() => onDragStart(lead.id)}
      className="bg-surface rounded-[10px] p-3 mb-2 cursor-grab active:cursor-grabbing"
      style={{
        boxShadow: '0 1px 3px rgba(13,23,41,0.07)',
        border: stale ? '1px solid rgba(217,119,6,0.4)' : '1px solid transparent',
      }}
    >
      <div className="flex items-start gap-2 mb-2">
        <AvatarInitials name={`${lead.firstName} ${lead.lastName}`} size={28} />
        <div className="flex-1 min-w-0">
          <p className="text-[12.5px] font-semibold text-ink truncate">
            {lead.firstName} {lead.lastName}
          </p>
          <p
            className="text-[10.5px] text-muted"
            style={{ fontFamily: '"DM Mono", monospace' }}
          >
            ${lead.dealValue.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Chips */}
      <div className="flex flex-wrap gap-1">
        {lead.state && (
          <span className="px-1.5 py-0.5 rounded-chip text-[9px] font-medium bg-surface-alt text-muted">
            {lead.state}
          </span>
        )}
        <span className="px-1.5 py-0.5 rounded-chip text-[9px] font-medium bg-surface-alt text-muted capitalize">
          {lead.source}
        </span>
        {stale && (
          <span
            className="px-1.5 py-0.5 rounded-chip text-[9px] font-semibold"
            style={{ background: 'rgba(217,119,6,0.1)', color: '#D97706' }}
          >
            Stale
          </span>
        )}
      </div>

      {/* Footer: last-touch age */}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[10px] text-muted">
          last touch
        </span>
        <span
          className="text-[10px] text-muted"
          style={{ fontFamily: '"DM Mono", monospace' }}
        >
          {age}
        </span>
      </div>
    </div>
  );
}

// ── Kanban column ─────────────────────────────────────────────────────────────

function KanbanColumn({
  stage,
  leads,
  draggingId,
  onDrop,
  onDragStart,
}: {
  stage: typeof STAGES[0];
  leads: ApiLead[];
  draggingId: string | null;
  onDrop: (targetStage: LeadStage) => void;
  onDragStart: (id: string) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const total = leads.reduce((sum, l) => sum + l.dealValue, 0);

  return (
    <div
      className="flex-shrink-0 rounded-[14px] flex flex-col"
      style={{
        width: 200,
        background: dragOver ? `${stage.color}0a` : '#F2F4F7',
        border: dragOver ? `1px solid ${stage.color}60` : '1px solid transparent',
        minHeight: 400,
        transition: 'background 0.15s, border 0.15s',
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onDrop(stage.key);
      }}
    >
      {/* Column header */}
      <div className="px-3 pt-3 pb-2 flex-shrink-0">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: stage.color }} />
          <span
            className="text-[12px] font-semibold text-ink truncate"
            style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
          >
            {stage.label}
          </span>
          <span
            className="ml-auto text-[10px] font-medium text-muted rounded-chip px-1.5 py-0.5 flex-shrink-0"
            style={{ background: '#E5EAF2', fontFamily: '"DM Mono", monospace' }}
          >
            {leads.length}
          </span>
        </div>
        {total > 0 && (
          <p
            className="text-[10px] text-muted"
            style={{ fontFamily: '"DM Mono", monospace' }}
          >
            ${total.toLocaleString()}
          </p>
        )}
      </div>

      {/* Cards */}
      <div className="flex-1 px-2 pb-2 overflow-y-auto">
        {leads.map((lead) => (
          <div key={lead.id} style={{ opacity: draggingId === lead.id ? 0.4 : 1 }}>
            <PipelineCard lead={lead} onDragStart={onDragStart} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Pipeline Page ────────────────────────────────────────────────────────

interface PipelinePageProps {
  aiName: string;
}

export function PipelinePage({ aiName }: PipelinePageProps) {
  const [filter, setFilter] = useState<FilterKind>('all');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const qc = useQueryClient();
  const { caps, guardWrite } = usePortal();

  const { data } = useQuery({
    queryKey: ['leads', { pageSize: 200 }],
    queryFn: () => fetchLeads({ pageSize: 200 }),
  });

  const allLeads = data?.data ?? [];

  // Apply filter
  const filtered = allLeads.filter((l) => {
    if (filter === 'ai') return !l.aiPaused;
    if (filter === 'stale') return isStale(l);
    if (filter === 'high') return l.dealValue >= 2000;
    return true;
  });

  // Group by stage
  const byStage = STAGES.reduce<Record<LeadStage, ApiLead[]>>((acc, s) => {
    acc[s.key] = filtered.filter((l) => l.stage === s.key);
    return acc;
  }, {} as Record<LeadStage, ApiLead[]>);

  const moveStage = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: LeadStage }) =>
      patchLead(id, { stage }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: () => {
      guardWrite(false, 'Pipeline edits unlock with Workspace CRM.');
    },
  });

  const handleDrop = (targetStage: LeadStage) => {
    if (!draggingId) return;
    const lead = allLeads.find((l) => l.id === draggingId);
    if (!lead || lead.stage === targetStage) {
      setDraggingId(null);
      return;
    }
    if (!guardWrite(caps.canDispositionLeads, 'View-only in managed mode — unlock Workspace CRM to move stages.')) {
      setDraggingId(null);
      return;
    }
    moveStage.mutate({ id: draggingId, stage: targetStage });
    setDraggingId(null);
  };

  const staleCount = allLeads.filter(isStale).length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1
            className="text-[22px] font-bold text-ink tracking-[-0.01em]"
            style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
          >
            Pipeline
          </h1>
          <p className="text-[13.5px] text-muted mt-0.5 flex items-center gap-1">
            Drag cards between stages —
            <span className="text-violet-DEFAULT">✦</span>
            {aiName} adapts the sequence when you move one
          </p>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 mb-5">
        {([
          { key: 'all', label: 'All' },
          { key: 'ai', label: `✦ ${aiName} working` },
          { key: 'stale', label: `Stale ${staleCount}` },
          { key: 'high', label: 'High value' },
        ] as { key: FilterKind; label: string }[]).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className="px-3 py-1.5 rounded-chip text-[11.5px] font-medium transition-colors"
            style={
              filter === key
                ? { background: '#0D1729', color: 'white' }
                : { background: '#F2F4F7', color: '#64748B', border: '1px solid #E5EAF2' }
            }
          >
            {label}
          </button>
        ))}
      </div>

      {/* Kanban board */}
      <div
        className="flex gap-3 overflow-x-auto pb-4"
        onDragEnd={() => setDraggingId(null)}
      >
        {STAGES.map((stage) => (
          <KanbanColumn
            key={stage.key}
            stage={stage}
            leads={byStage[stage.key]}
            draggingId={draggingId}
            onDragStart={setDraggingId}
            onDrop={handleDrop}
          />
        ))}
      </div>
    </div>
  );
}
